// b2 SEI extractor — parses mpengine's user_data_unregistered SEI NAL out
// of an MP4 video sample.
//
// Producer reference: nvr-feature-dragon-pilot/mpengine/src/b2.c (build_h264_sei_nal,
// build_hevc_sei_nal, escape_sei_payload).
// Consumer reference: nvr-feature-dragon-pilot/dview-ffmpeg/csrc/ffmpeg/B2.c
// (parse_b2_header, parse_b2, unescape_sei_payload).
//
// Two-stage byte transform on read:
//   1. NAL-level: find SEI NAL by nal_type (codec-dispatched). MP4 stores
//      NALs length-prefixed without standard AnnexB RBSP escaping for
//      mpengine output, so we do NOT apply RBSP unescape by default — that
//      would corrupt valid 00 00 03 byte sequences in the payload. Pass
//      {rbspUnescape: true} only when the input is known to be RBSP-encoded
//      (e.g. AnnexB stream from a non-mpengine producer).
//   2. Payload-level: mpengine's escape_sei_payload rewrites 00 00 01 → AA AA AB
//      in the b2 body before NAL wrap. We reverse this after UUID match,
//      before B2_HEADER_DVS parse.
import { B2_EXT } from "./types";
// UUID mpengine writes into every user_data_unregistered SEI to identify
// the payload as ours (`uuid_iso_iec_11578` at b2.c:434).
export const B2_UUID = new Uint8Array([
    0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF,
    0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF, 0xAA, 0xFF,
]);
// 00 00 01 b2 — the dvs[4] prefix mpengine writes at b2.c:141-142. After
// payload unescape, the b2 header starts with these bytes.
const B2_HEADER_PREFIX = new Uint8Array([0x00, 0x00, 0x01, 0xb2]);
// Size of the V2 B2_HEADER_DVS packed struct (see b2.h:50-60).
//   dvs[4] + version(1) + rec_flag(1) + ext_flag(1) + vmd(1) + timestamp(4)
//   + name[32] + gmtoffset(1) + padding[3] = 48 bytes
export const B2_HEADER_V2_SIZE = 48;
// Size of B2_HEADER_DVS_V1 (b2.h:40-48).
//   dvs[4] + version(1) + rec_flag(1) + ext_flag(1) + vmd(1) + timestamp(4)
//   + name[34] = 46 bytes
export const B2_HEADER_V1_SIZE = 46;
// Anti-runaway guard on the extension loop. mpengine writes at most
// (OVERLAY+DRIVE+POS+VMD+VMD2+LPR+WATERMARK+OD)*1 = 8 unique extension types,
// plus multiple VMD windows / POS terminals. 32 caps the worst-case fleet
// config (many VMD windows) without running unbounded.
const MAX_EXT_LOOP = 32;
// Decode bytes through a fault-tolerant UTF-8 reader, slicing to the first
// null byte. Matches decision [#11]: "slice to first null OR 32, TextDecoder
// utf-8 fatal:false." Bad bytes → U+FFFD instead of throwing.
function decodeCString(bytes) {
    const nullIdx = bytes.indexOf(0);
    const end = nullIdx === -1 ? bytes.length : nullIdx;
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes.subarray(0, end));
}
// Strip H.264/HEVC RBSP emulation-prevention bytes (0x03) from a byte
// sequence: every 00 00 03 → 00 00. The trailing rbsp_trailing_bits byte
// is left as-is (parsers downstream tolerate it).
export function unescapeRbsp(bytes) {
    const out = new Uint8Array(bytes.length);
    let o = 0;
    for (let i = 0; i < bytes.length; i++) {
        if (i + 2 < bytes.length &&
            bytes[i] === 0x00 &&
            bytes[i + 1] === 0x00 &&
            bytes[i + 2] === 0x03) {
            out[o++] = 0x00;
            out[o++] = 0x00;
            i += 2; // skip the 0x03
        }
        else {
            out[o++] = bytes[i];
        }
    }
    return out.subarray(0, o);
}
// Reverse mpengine's escape_sei_payload (b2.c:570). Every AA AA AB → 00 00 01.
// In-place semantics in C; we return a new Uint8Array here to keep callers
// from accidentally mutating shared SEI bytes (the source MP4 buffer).
export function unescapeB2Payload(bytes) {
    const out = new Uint8Array(bytes.length);
    let o = 0;
    for (let i = 0; i < bytes.length; i++) {
        if (i + 2 < bytes.length &&
            bytes[i] === 0xAA &&
            bytes[i + 1] === 0xAA &&
            bytes[i + 2] === 0xAB) {
            out[o++] = 0x00;
            out[o++] = 0x00;
            out[o++] = 0x01;
            i += 2;
        }
        else {
            out[o++] = bytes[i];
        }
    }
    return out.subarray(0, o);
}
// Walk an MP4 sample (length-prefixed NAL units) or AnnexB byte stream and
// return the first user_data_unregistered SEI that matches our UUID.
// Bails early (decision [#17]) after first SEI hit OR first VCL NAL.
//
// Length-prefix detection: if `bytes` starts with 00 00 01 or 00 00 00 01
// we treat it as AnnexB; otherwise we assume MP4 length-prefixed with a
// 4-byte big-endian length. This matches Brad's sliceSample output, which
// returns the mdat-resident NAL bytes verbatim from MP4 (length-prefixed).
export function findSeiInSample(bytes, codec, opts = {}) {
    if (bytes.length < 5)
        return null;
    const isAnnexB = (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01) ||
        (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x00 && bytes[3] === 0x01);
    if (isAnnexB) {
        return findSeiAnnexB(bytes, codec, opts);
    }
    return findSeiLengthPrefixed(bytes, codec, opts);
}
function findSeiLengthPrefixed(bytes, codec, opts) {
    let p = 0;
    while (p + 4 < bytes.length) {
        const nalSize = (bytes[p] << 24) | (bytes[p + 1] << 16) | (bytes[p + 2] << 8) | bytes[p + 3];
        p += 4;
        if (nalSize <= 0 || p + nalSize > bytes.length)
            return null;
        const nalEnd = p + nalSize;
        const nalBody = bytes.subarray(p, nalEnd);
        if (codec === "h264") {
            const nalType = nalBody[0] & 0x1F;
            if (nalType === 6 /* SEI */) {
                const sei = tryParseSei(nalBody, 1, codec, opts);
                if (sei)
                    return sei;
            }
            else if (nalType === 1 || nalType === 5 /* VCL slice */) {
                return null; // bail-early
            }
        }
        else {
            // HEVC: 2-byte NAL header, nal_type = (byte0 >> 1) & 0x3F
            const nalType = (nalBody[0] >> 1) & 0x3F;
            if (nalType === 39 || nalType === 40 /* prefix/suffix SEI */) {
                const sei = tryParseSei(nalBody, 2, codec, opts);
                if (sei)
                    return sei;
            }
            else if (nalType <= 31 /* VCL slice */) {
                return null;
            }
        }
        p = nalEnd;
    }
    return null;
}
function findSeiAnnexB(bytes, codec, opts) {
    // Find NAL start positions by walking 00 00 00 01 or 00 00 01.
    const starts = [];
    let p = 0;
    while (p + 3 <= bytes.length) {
        if (bytes[p] === 0x00 &&
            bytes[p + 1] === 0x00 &&
            bytes[p + 2] === 0x00 &&
            bytes[p + 3] === 0x01) {
            starts.push({ offset: p + 4, hdrLen: 4 });
            p += 4;
        }
        else if (bytes[p] === 0x00 &&
            bytes[p + 1] === 0x00 &&
            bytes[p + 2] === 0x01) {
            starts.push({ offset: p + 3, hdrLen: 3 });
            p += 3;
        }
        else {
            p++;
        }
    }
    for (let i = 0; i < starts.length; i++) {
        const start = starts[i].offset;
        const end = i + 1 < starts.length ? starts[i + 1].offset - starts[i + 1].hdrLen : bytes.length;
        const nalBody = bytes.subarray(start, end);
        if (codec === "h264") {
            const nalType = nalBody[0] & 0x1F;
            if (nalType === 6) {
                const sei = tryParseSei(nalBody, 1, codec, opts);
                if (sei)
                    return sei;
            }
            else if (nalType === 1 || nalType === 5) {
                return null;
            }
        }
        else {
            const nalType = (nalBody[0] >> 1) & 0x3F;
            if (nalType === 39 || nalType === 40) {
                const sei = tryParseSei(nalBody, 2, codec, opts);
                if (sei)
                    return sei;
            }
            else if (nalType <= 31) {
                return null;
            }
        }
    }
    return null;
}
// nalBody starts at the NAL header byte. headerLen is 1 (H.264) or 2 (HEVC).
// Returns the SEI payload AFTER the UUID, or null if SEI doesn't match.
function tryParseSei(nalBody, headerLen, codec, opts) {
    const body = opts.rbspUnescape ? unescapeRbsp(nalBody) : nalBody;
    if (body.length < headerLen + 2)
        return null;
    let p = headerLen;
    // SEI message: payload_type (ff*N + final), payload_size (ff*N + final), payload bytes.
    // Standard SEI allows multiple messages per NAL; mpengine only writes one.
    let payloadType = 0;
    while (p < body.length && body[p] === 0xFF) {
        payloadType += 0xFF;
        p++;
    }
    if (p >= body.length)
        return null;
    payloadType += body[p];
    p++;
    if (payloadType !== 5)
        return null; // user_data_unregistered only
    let payloadSize = 0;
    while (p < body.length && body[p] === 0xFF) {
        payloadSize += 0xFF;
        p++;
    }
    if (p >= body.length)
        return null;
    payloadSize += body[p];
    p++;
    // Need UUID + payload bytes.
    if (p + 16 > body.length)
        return null;
    for (let i = 0; i < 16; i++) {
        if (body[p + i] !== B2_UUID[i])
            return null;
    }
    p += 16;
    // payloadSize counts UUID + user_data. user_data length is payloadSize - 16.
    const userDataLen = Math.max(0, payloadSize - 16);
    const end = Math.min(p + userDataLen, body.length);
    return { codec, payload: body.subarray(p, end) };
}
// Parse a V2 (or V1 fallback) B2_HEADER_DVS from already-unescaped bytes.
// The bytes here are the b2 payload AFTER unescapeB2Payload; the header
// starts at offset 0 with dvs = [0x00, 0x00, 0x01, 0xb2].
export function parseB2Header(bytes) {
    if (bytes.length < 5)
        return { fallback: true, version: -1 };
    // Validate dvs[4] prefix. mpengine writes [0, 0, 0x01, 0xb2] at b2.c:141-142.
    // If absent we're not pointing at a real b2 header.
    if (bytes[0] !== B2_HEADER_PREFIX[0] ||
        bytes[1] !== B2_HEADER_PREFIX[1] ||
        bytes[2] !== B2_HEADER_PREFIX[2] ||
        bytes[3] !== B2_HEADER_PREFIX[3]) {
        return { fallback: true, version: bytes[4] };
    }
    const version = bytes[4];
    if (version !== 0x02) {
        // V1 or unknown: decision [#3] — log once + fall back to event-row HUD.
        return { fallback: true, version };
    }
    if (bytes.length < B2_HEADER_V2_SIZE) {
        return { fallback: true, version };
    }
    const rec_flag = bytes[5];
    const ext_flag = bytes[6];
    const vmd = bytes[7];
    // timestamp is u32 LE (mpengine: x86-64 host order, packed struct).
    const timestamp = bytes[8] |
        (bytes[9] << 8) |
        (bytes[10] << 16) |
        ((bytes[11] << 24) >>> 0);
    const name = decodeCString(bytes.subarray(12, 12 + 32));
    // gmtoffset is signed (hours offset can be negative).
    const gmtoffset = (bytes[44] << 24) >> 24;
    // padding[3] bytes 45-47 ignored
    return {
        version,
        rec_flag,
        ext_flag,
        vmd,
        timestamp: timestamp >>> 0,
        name,
        gmtoffset,
    };
}
// Read u16 little-endian (mpengine packed structs are host order = LE on x86_64).
function readU16LE(bytes, o) {
    return (bytes[o] | (bytes[o + 1] << 8)) >>> 0;
}
function readU32LE(bytes, o) {
    return ((bytes[o] |
        (bytes[o + 1] << 8) |
        (bytes[o + 2] << 16) |
        (bytes[o + 3] << 24)) >>>
        0);
}
// Sizes of each fixed-length extension struct, in bytes. Variable-length
// (OVERLAY / POS) read their dynamic size from line_width * line_count after
// the header.
const EXT_SIZES = {
    // VMD (b2.h:62-75): ext_id(1) + version(1) + window_id(1) + sx/sy/ex/ey(8)
    // + sens[5]*2(10) + trigger[5](5) + motion_level(2) + tripwire(1) + directional(1) = 30
    VMD: 30,
    // LPR (b2.h:77-97, packed): ext_id(1) + version(1) + last_hit_lpn[32] + lpn[32]
    // + confidence(2) + state(1) + detection_time(2) + 8*p_*(16) + 4*roi_*(8) = 95.
    // (NOT 103 — the trailing 8 bytes don't exist; misalignment loses any
    // extension that follows LPR in a clip.)
    LPR: 95,
    // OD V1 (b2.h:100-109): ext_id(1) + version(1) + x/y/w/h(8) + cls(2) + reserved(2) = 14
    OD_V1: 14,
    // OD V2 (b2.h:112-123): ext_id(1) + version(1) + x/y/w/h(8) + cls_name[32]
    // + cls(2) + flags(2) + color(4) = 50
    OD_V2: 50,
    // WATERMARK (b2.h:126-131): ext_id(1) + version(1) + hash(4) + hash2(4) = 10
    WATERMARK: 10,
    // OVERLAY header (b2.h:135-142): ext_id(1) + pos_id(1) + line_width(1)
    // + line_count(1) + x_loc(4) + y_loc(4) = 12, then line_width*line_count text bytes
    OVERLAY_HDR: 12,
    // DRIVE (b2.h:144-150, NOT packed): the compiler inserts 2 bytes of padding
    // between version (offset 1) and x_loc (must be 4-byte aligned). Wire size on
    // x86_64 Linux mpengine = ext_id(1) + version(1) + pad(2) + x_loc(4) + y_loc(4)
    // + timestr[32] = 44 bytes.
    DRIVE: 44,
    // POS header (b2.h:152-162): ext_id(1) + version(1) + pos_id(1)
    // + line_width(1) + line_count(1) + res[3] + x_loc(4) + y_loc(4) + name[32] = 48
    // then line_width*line_count text bytes
    POS_HDR: 48,
};
function parseVmd(bytes, p) {
    const version = bytes[p + 1];
    const window_id = bytes[p + 2];
    const sx = readU16LE(bytes, p + 3);
    const sy = readU16LE(bytes, p + 5);
    const ex = readU16LE(bytes, p + 7);
    const ey = readU16LE(bytes, p + 9);
    const sens = [];
    for (let i = 0; i < 5; i++)
        sens.push(readU16LE(bytes, p + 11 + i * 2));
    const trigger = [];
    for (let i = 0; i < 5; i++)
        trigger.push(bytes[p + 21 + i]);
    const motion_level = readU16LE(bytes, p + 26);
    const tripwire = bytes[p + 28];
    const directional = bytes[p + 29];
    return { version, window_id, sx, sy, ex, ey, sens, trigger, motion_level, tripwire, directional };
}
function parseLpr(bytes, p) {
    const version = bytes[p + 1];
    const last_hit_lpn = decodeCString(bytes.subarray(p + 2, p + 34));
    const lpn = decodeCString(bytes.subarray(p + 34, p + 66));
    const confidence = readU16LE(bytes, p + 66);
    const state = bytes[p + 68];
    const detection_time = readU16LE(bytes, p + 69);
    const p1_x = readU16LE(bytes, p + 71);
    const p1_y = readU16LE(bytes, p + 73);
    const p2_x = readU16LE(bytes, p + 75);
    const p2_y = readU16LE(bytes, p + 77);
    const p3_x = readU16LE(bytes, p + 79);
    const p3_y = readU16LE(bytes, p + 81);
    const p4_x = readU16LE(bytes, p + 83);
    const p4_y = readU16LE(bytes, p + 85);
    const roi_sx = readU16LE(bytes, p + 87);
    const roi_sy = readU16LE(bytes, p + 89);
    const roi_ex = readU16LE(bytes, p + 91);
    const roi_ey = readU16LE(bytes, p + 93);
    return {
        version, last_hit_lpn, lpn, confidence, state, detection_time,
        p1_x, p1_y, p2_x, p2_y, p3_x, p3_y, p4_x, p4_y,
        roi_sx, roi_sy, roi_ex, roi_ey,
    };
}
function parseOverlay(bytes, p) {
    const pos_id = bytes[p + 1];
    const line_width = bytes[p + 2];
    const line_count = bytes[p + 3];
    const x_loc = readU32LE(bytes, p + 4);
    const y_loc = readU32LE(bytes, p + 8);
    const linesStart = p + EXT_SIZES.OVERLAY_HDR;
    const linesLen = line_width * line_count;
    // Bounds check: untrusted line_width/line_count from the SEI. Reject if
    // the claimed body exceeds the remaining buffer — otherwise we'd push
    // dozens of empty strings or desync the cursor for trailing extensions.
    if (linesStart + linesLen > bytes.length)
        return null;
    const lines = [];
    for (let i = 0; i < line_count; i++) {
        const lineStart = linesStart + i * line_width;
        const lineEnd = lineStart + line_width;
        lines.push(decodeCString(bytes.subarray(lineStart, lineEnd)));
    }
    return {
        ext: { pos_id, line_width, line_count, x_loc, y_loc, lines },
        size: EXT_SIZES.OVERLAY_HDR + linesLen,
    };
}
function parseDrive(bytes, p) {
    // B2_EXT_DRIVE is NOT packed (b2.h:144-150). The compiler inserts 2 bytes
    // of padding between `version` (offset 1) and `x_loc` (4-byte aligned at
    // offset 4). timestr starts at offset 12.
    const version = bytes[p + 1];
    const x_loc = readU32LE(bytes, p + 4);
    const y_loc = readU32LE(bytes, p + 8);
    const timestr = decodeCString(bytes.subarray(p + 12, p + 44));
    return { version, x_loc, y_loc, timestr };
}
function parsePos(bytes, p) {
    const version = bytes[p + 1];
    const pos_id = bytes[p + 2];
    const line_width = bytes[p + 3];
    const line_count = bytes[p + 4];
    // res[3] at p+5..p+7
    const x_loc = readU32LE(bytes, p + 8);
    const y_loc = readU32LE(bytes, p + 12);
    const name = decodeCString(bytes.subarray(p + 16, p + 48));
    const linesStart = p + EXT_SIZES.POS_HDR;
    const linesLen = line_width * line_count;
    // Bounds check (see parseOverlay).
    if (linesStart + linesLen > bytes.length)
        return null;
    const lines = [];
    for (let i = 0; i < line_count; i++) {
        const lineStart = linesStart + i * line_width;
        const lineEnd = lineStart + line_width;
        lines.push(decodeCString(bytes.subarray(lineStart, lineEnd)));
    }
    return {
        ext: { version, pos_id, line_width, line_count, x_loc, y_loc, name, lines },
        size: EXT_SIZES.POS_HDR + linesLen,
    };
}
function parseWatermark(bytes, p) {
    const version = bytes[p + 1];
    const hash = readU32LE(bytes, p + 2);
    const hash2 = readU32LE(bytes, p + 6);
    return { version, hash, hash2 };
}
function parseOd(bytes, p) {
    const version = bytes[p + 1];
    if (version === 1) {
        const x = readU16LE(bytes, p + 2);
        const y = readU16LE(bytes, p + 4);
        const width = readU16LE(bytes, p + 6);
        const height = readU16LE(bytes, p + 8);
        const cls = (readU16LE(bytes, p + 10) << 16) >> 16; // sign-extend int16
        return { ext: { version: 1, x, y, width, height, cls }, size: EXT_SIZES.OD_V1 };
    }
    if (version === 2) {
        const x = readU16LE(bytes, p + 2);
        const y = readU16LE(bytes, p + 4);
        const width = readU16LE(bytes, p + 6);
        const height = readU16LE(bytes, p + 8);
        const cls_name = decodeCString(bytes.subarray(p + 10, p + 42));
        const cls = (readU16LE(bytes, p + 42) << 16) >> 16;
        const flags = (readU16LE(bytes, p + 44) << 16) >> 16;
        const color = readU32LE(bytes, p + 46);
        return {
            ext: { version: 2, x, y, width, height, cls_name, cls, flags, color },
            size: EXT_SIZES.OD_V2,
        };
    }
    return null;
}
// Walk the extension byte stream after the B2_HEADER_DVS struct. Decision [#21]:
// dispatch on ext_id at bytes[cursor], NOT on bit position in extFlag. Each
// extension struct starts with `char ext_id` (b2.h:64+). Bit-order walk is
// wrong for any non-1-byte extension, and the bug is silent.
export function parseB2Extensions(bytes, extFlag) {
    const out = {};
    let cursor = 0;
    let loops = 0;
    while (cursor < bytes.length && loops++ < MAX_EXT_LOOP) {
        const id = bytes[cursor];
        if (id === 0x00)
            break;
        // Note: the ext_id might appear in the stream even if extFlag's bit is
        // clear (legacy / paranoia). Trust the stream byte; extFlag is advisory.
        void extFlag;
        if (id === B2_EXT.OVERLAY) {
            const result = parseOverlay(bytes, cursor);
            if (!result)
                break;
            out.overlay = result.ext;
            cursor += result.size;
        }
        else if (id === B2_EXT.DRIVE) {
            if (cursor + EXT_SIZES.DRIVE > bytes.length)
                break;
            out.drive = parseDrive(bytes, cursor);
            cursor += EXT_SIZES.DRIVE;
        }
        else if (id === B2_EXT.POS) {
            const result = parsePos(bytes, cursor);
            if (!result)
                break;
            (out.pos ??= []).push(result.ext);
            cursor += result.size;
        }
        else if (id === B2_EXT.VMD) {
            if (cursor + EXT_SIZES.VMD > bytes.length)
                break;
            (out.vmd ??= []).push(parseVmd(bytes, cursor));
            cursor += EXT_SIZES.VMD;
        }
        else if (id === B2_EXT.VMD2) {
            if (cursor + EXT_SIZES.VMD > bytes.length)
                break;
            (out.vmd2 ??= []).push(parseVmd(bytes, cursor));
            cursor += EXT_SIZES.VMD;
        }
        else if (id === B2_EXT.LPR) {
            if (cursor + EXT_SIZES.LPR > bytes.length)
                break;
            out.lpr = parseLpr(bytes, cursor);
            cursor += EXT_SIZES.LPR;
        }
        else if (id === B2_EXT.WATERMARK) {
            if (cursor + EXT_SIZES.WATERMARK > bytes.length)
                break;
            out.watermark = parseWatermark(bytes, cursor);
            cursor += EXT_SIZES.WATERMARK;
        }
        else if (id === B2_EXT.OD) {
            const od = parseOd(bytes, cursor);
            if (!od)
                break;
            (out.od ??= []).push(od.ext);
            cursor += od.size;
        }
        else {
            // Unknown ext_id — stop, don't try to recover (we'd misalign the stream).
            break;
        }
    }
    return out;
}
// One-call helper used by Playback: take raw sample bytes, find SEI, unescape,
// parse header + extensions. Returns null if no valid b2 SEI present.
let _v1FallbackLogged = false;
export function parseB2FromSample(sampleBytes, codec, opts = {}) {
    const sei = findSeiInSample(sampleBytes, codec, opts);
    if (!sei)
        return null;
    const unescaped = unescapeB2Payload(sei.payload);
    const headerResult = parseB2Header(unescaped);
    if ("fallback" in headerResult) {
        if (!_v1FallbackLogged) {
            _v1FallbackLogged = true;
            // eslint-disable-next-line no-console
            console.warn(`[b2-sei] header version=${headerResult.version} not supported — falling back to event-row HUD`);
        }
        return null;
    }
    const extensions = parseB2Extensions(unescaped.subarray(B2_HEADER_V2_SIZE), headerResult.ext_flag);
    return { header: headerResult, extensions };
}
// For tests: reset the once-per-process V1 fallback warning state.
export function _resetV1FallbackLog() {
    _v1FallbackLogged = false;
}
